import { returnZero } from '@ardoq/common-helpers';
import { fontFamily } from '@ardoq/design-tokens';
import { ComponentLabelParts, truncateComponentLabel } from '@ardoq/graph';
import { canvasLabelMeasurer } from '@ardoq/graph-labeling';
import {
  AXIS_LABEL_HEIGHT,
  DATAPOINT_RADIUS,
  LABEL_MAX_LENGTH,
  LABEL_PADDING,
  NINETY_DEGREES,
} from './consts';
import { ChartConfiguration } from './types';
import { getLabelTextAnchor, indexToRadians } from './utils';

export type LabelState = 'full' | 'ellipsis';

export type LabelLayoutConfig = Pick<
  ChartConfiguration,
  'centerX' | 'centerY' | 'radius' | 'total'
>;

export const DEFAULT_FONT_SIZE = 14;
const LABEL_LINE_HEIGHT = 20; // from font spec "14px/20px"
const MIN_FONT_SIZE = 10;

export const SPIDER_CHART_LABEL_FONT = `400 ${DEFAULT_FONT_SIZE}px/${LABEL_LINE_HEIGHT}px ${fontFamily.regular}`;
export const measureLabel = canvasLabelMeasurer(SPIDER_CHART_LABEL_FONT);

/** Extra space around each label bounding box to prevent labels from touching. */
const BBOX_PADDING = 4;

/**
 * Returns the largest integer font size (clamped to [MIN_FONT_SIZE,
 * DEFAULT_FONT_SIZE]) at which adjacent axis-label bboxes at the worst-case
 * east/west position of the chart just do not overlap.
 *
 * Derivation: at the equator (θ≈0), the vertical gap between adjacent label
 * centres is r·sin(2π/N) where r = radius + DATAPOINT_RADIUS + LABEL_PADDING.
 * For bboxes of height (h + 2·BBOX_PADDING) not to overlap:
 *   (r + h/2)·s ≥ h + 2·BBOX_PADDING   where s = sin(2π/N), h = line-height
 * Solving for h and converting to font size via h = fontSize·(LABEL_LINE_HEIGHT/DEFAULT_FONT_SIZE).
 */
export const getAdaptiveFontSize = (radius: number, total: number): number => {
  if (total <= 2) return DEFAULT_FONT_SIZE;
  const s = Math.abs(Math.sin((2 * Math.PI) / total));
  const radialOffset = DATAPOINT_RADIUS + LABEL_PADDING;
  const h = (s * (radius + radialOffset) - 2 * BBOX_PADDING) / (1 - s / 2);
  const fontSize = (h * DEFAULT_FONT_SIZE) / LABEL_LINE_HEIGHT;
  return Math.max(
    MIN_FONT_SIZE,
    Math.min(DEFAULT_FONT_SIZE, Math.floor(fontSize))
  );
};

type LabelBBox = [xMin: number, xMax: number, yMin: number, yMax: number];

const getBBox = (
  x: number,
  y: number,
  labelWidth: number,
  textAnchor: 'start' | 'end' | 'middle',
  axisLabelHeight: number
): LabelBBox => {
  const left =
    textAnchor === 'end'
      ? x - labelWidth
      : textAnchor === 'middle'
        ? x - labelWidth / 2
        : x;
  return [
    left - BBOX_PADDING,
    left + labelWidth + BBOX_PADDING,
    y - axisLabelHeight / 2 - BBOX_PADDING,
    y + axisLabelHeight / 2 + BBOX_PADDING,
  ];
};

const overlaps = (a: LabelBBox, b: LabelBBox): boolean => {
  const dx = Math.min(a[1], b[1]) - Math.max(a[0], b[0]);
  const dy = Math.min(a[3], b[3]) - Math.max(a[2], b[2]);
  return dx > 0 && dy > 0;
};

const getMeasuredWidth = (labelInfo: ComponentLabelParts): number => {
  const fullLabel = truncateComponentLabel({
    ...labelInfo,
    measure: returnZero,
    width: Infinity,
  });
  const clipped =
    fullLabel.length > LABEL_MAX_LENGTH
      ? `${fullLabel.slice(0, LABEL_MAX_LENGTH)}...`
      : fullLabel;
  return measureLabel(clipped);
};

/**
 * Determines how each axis label should render: 'full' text or '…' with a
 * popover. Every axis always shows at least '…'.
 *
 * All axis-tip positions (ellipsis-sized bboxes) are pre-computed as
 * obstacles, so a full-text label is only accepted if it doesn't sweep over
 * any other axis's tip. Accepted full-text bboxes are also tracked to prevent
 * two full-text labels from overlapping each other. '…' labels are always
 * rendered and never block subsequent labels.
 */
export const computeLabelStates = (
  labelsInfo: ComponentLabelParts[],
  config: LabelLayoutConfig
): LabelState[] => {
  const { centerX, centerY, radius, total } = config;
  const n = labelsInfo.length;
  if (n === 0) return [];

  const adaptiveFontSize = getAdaptiveFontSize(radius, total);
  const widthScale = adaptiveFontSize / DEFAULT_FONT_SIZE;
  // Scale AXIS_LABEL_HEIGHT proportionally so bbox height matches the smaller font.
  const adaptiveAxisLabelHeight = Math.round(AXIS_LABEL_HEIGHT * widthScale);
  const baseRadialOffset =
    AXIS_LABEL_HEIGHT / 2 + DATAPOINT_RADIUS + LABEL_PADDING;
  const fullWidths = labelsInfo.map(
    labelInfo => getMeasuredWidth(labelInfo) * widthScale
  );
  const textAnchors = labelsInfo.map((_, i) => getLabelTextAnchor(i, total));

  const getAxisBBox = (i: number, width: number): LabelBBox => {
    const angle = indexToRadians(i, total) - NINETY_DEGREES;
    const r = radius + baseRadialOffset;
    return getBBox(
      centerX + r * Math.cos(angle),
      centerY + r * Math.sin(angle),
      width,
      textAnchors[i],
      adaptiveAxisLabelHeight
    );
  };

  const ellipsisWidth = measureLabel('…') * widthScale;
  const tipBBoxes = labelsInfo.map((_, i) => getAxisBBox(i, ellipsisWidth));

  // Circular distance between two axis indices.
  const circularDist = (a: number, b: number) =>
    Math.min(Math.abs(a - b), n - Math.abs(a - b));

  const states = new Array<LabelState>(n).fill('ellipsis');
  const acceptedBBoxes: LabelBBox[] = [];

  for (let i = 0; i < n; i++) {
    const fullBBox = getAxisBBox(i, fullWidths[i]);
    // Skip the immediately adjacent labels (±1) when checking tip bboxes:
    // adjacent tips are always physically close and their bboxes overlap the
    // current label's bbox even when there's no visual collision. Non-adjacent
    // tips (dist ≥ 2) represent genuine label positions that the full text
    // should not sweep over.
    const collides =
      acceptedBBoxes.some(a => overlaps(fullBBox, a)) ||
      tipBBoxes.some(
        (tip, j) => circularDist(i, j) >= 2 && overlaps(fullBBox, tip)
      );
    if (!collides) {
      states[i] = 'full';
      acceptedBBoxes.push(fullBBox);
    }
    // else: 'ellipsis' — tip bboxes are pre-computed, not added again
  }

  return states;
};
